import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, RotateCcw, X } from "lucide-react";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SESSION_KEY = "weave_session_id";
const SESSION_BIZ_KEY = "weave_session_biz";

const REVENUE_RANGES = [
    "Pre-revenue",
    "Under £50k",
    "£50k – £250k",
    "£250k – £1m",
    "£1m – £5m",
    "£5m – £20m",
    "£20m+",
];

const REGIONS = [
    "United Kingdom",
    "United States",
    "EU",
    "Rest of Europe",
    "Asia-Pacific",
    "Middle East / Africa",
    "Latin America",
    "Global",
];

// Phase tags for each question — used to label and colour each cell.
// Business flow: 8 questions (5 whys + 2 strengths + 1 constraint)
// Career flow: 7 questions (3 personal + 2 business + 2 external clarities)
const BUSINESS_PHASES = [
    { label: "Why 1", group: "whys" },
    { label: "Why 2", group: "whys" },
    { label: "Why 3", group: "whys" },
    { label: "Why 4", group: "whys" },
    { label: "Why 5", group: "whys" },
    { label: "Strength", group: "strengths" },
    { label: "Asset", group: "strengths" },
    { label: "Constraint", group: "constraint" },
];
const CAREER_PHASES = [
    { label: "Personal 1", group: "personal" },
    { label: "Personal 2", group: "personal" },
    { label: "Personal 3", group: "personal" },
    { label: "Business 1", group: "business" },
    { label: "Business 2", group: "business" },
    { label: "External 1", group: "external" },
    { label: "External 2", group: "external" },
];

function ProgressBar({ value, total, flow = "business" }) {
    const phases = flow === "career" ? CAREER_PHASES : BUSINESS_PHASES;
    const gridCols = flow === "career" ? "grid-cols-7" : "grid-cols-8";
    const groupColours =
        flow === "career"
            ? {
                  personal: { filled: "bg-weave-teal", current: "bg-weave-teal/30" },
                  business: { filled: "bg-weave-yellow", current: "bg-weave-yellow/30" },
                  external: { filled: "bg-weave-red", current: "bg-weave-red/30" },
              }
            : {
                  whys: { filled: "bg-weave-teal", current: "bg-weave-teal/30" },
                  strengths: { filled: "bg-weave-yellow", current: "bg-weave-yellow/30" },
                  constraint: { filled: "bg-weave-red", current: "bg-weave-red/30" },
              };
    const legend =
        flow === "career"
            ? [
                  { swatch: "bg-weave-teal", label: "Personal clarity" },
                  { swatch: "bg-weave-yellow", label: "Business clarity" },
                  { swatch: "bg-weave-red", label: "External clarity" },
              ]
            : [
                  { swatch: "bg-weave-teal", label: "Five whys" },
                  { swatch: "bg-weave-yellow", label: "Strengths" },
                  { swatch: "bg-weave-red", label: "Constraint" },
              ];

    return (
        <div className="w-full" data-testid="diagnostic-progress">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    <span className="editorial-label text-weave-ink">
                        Progress
                    </span>
                    <span className="text-xs text-weave-slate">
                        · auto-saved
                    </span>
                </div>
                <span className="text-sm text-weave-ink tabular-nums font-medium">
                    {value} <span className="text-weave-muted">/ {total}</span>
                </span>
            </div>
            <div className={`grid ${gridCols} gap-1.5`}>
                {Array.from({ length: total }).map((_, i) => {
                    const filled = i < value;
                    const isCurrent = i === value;
                    const phase = phases[i] || phases[0];
                    const c = groupColours[phase.group];
                    const colour = filled
                        ? c.filled
                        : isCurrent
                        ? c.current
                        : "bg-weave-border";
                    return (
                        <div
                            key={i}
                            className={`h-2 transition-colors duration-300 ${colour}`}
                            data-testid={`progress-cell-${i}`}
                            title={phase.label}
                        />
                    );
                })}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-editorial text-weave-muted">
                {legend.map((l) => (
                    <span key={l.label} className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 ${l.swatch}`} /> {l.label}
                    </span>
                ))}
            </div>
        </div>
    );
}

function ResumeBanner({ businessName, onResume, onDiscard }) {
    return (
        <div
            className="border border-weave-ink bg-weave-surface-alt p-6 md:p-8 mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4 animate-fade-in"
            data-testid="resume-banner"
        >
            <div>
                <p className="editorial-label text-weave-teal mb-1">
                    Saved conversation
                </p>
                <p className="text-weave-ink text-lg">
                    Pick up where you left off
                    {businessName ? (
                        <>
                            {" "}for{" "}
                            <span className="font-medium">{businessName}</span>
                        </>
                    ) : null}
                    .
                </p>
                <p className="text-sm text-weave-slate mt-1">
                    Your answers are all safe — just click resume.
                </p>
            </div>
            <div className="flex gap-3">
                <button
                    onClick={onDiscard}
                    className="btn-ghost"
                    data-testid="resume-discard"
                >
                    <X className="w-4 h-4" /> Start fresh
                </button>
                <button
                    onClick={onResume}
                    className="btn-primary"
                    data-testid="resume-continue"
                >
                    <RotateCcw className="w-4 h-4" /> Resume
                </button>
            </div>
        </div>
    );
}

export default function Diagnostic() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [phase, setPhase] = useState("intake"); // intake | conversation | generating
    const [flowType, setFlowType] = useState(
        searchParams.get("flow") === "business" ? "business" : "career"
    ); // "career" is now the default (career-first landing)
    const [intake, setIntake] = useState({
        // Business flow fields
        business_name: "",
        what_you_do: "",
        current_customers: "",
        revenue_range: "",
        region: "United Kingdom",
        plateau_description: "",
        mission_statement: "",
        // Career flow fields (initialized so inputs stay controlled)
        first_name: "",
        industry: "",
        tenure: "",
        restlessness: "",
    });
    const [sessionId, setSessionId] = useState(null);
    const [currentQ, setCurrentQ] = useState("");
    const [answer, setAnswer] = useState("");
    const [stepIndex, setStepIndex] = useState(0);
    const [totalSteps, setTotalSteps] = useState(8);
    const [submitting, setSubmitting] = useState(false);
    const [history, setHistory] = useState([]);
    const [resumeInfo, setResumeInfo] = useState(null);
    const [bootChecking, setBootChecking] = useState(true);
    const pollingRef = useRef(false);

    // Auto-save the answer-in-progress per session (so a refresh doesn't lose it)
    const draftKey = sessionId ? `weave_draft_${sessionId}` : null;
    useEffect(() => {
        if (!draftKey) return;
        if (answer) {
            try {
                localStorage.setItem(draftKey, answer);
            } catch {}
        }
    }, [answer, draftKey]);

    // On mount: check for a saved session
    useEffect(() => {
        const savedId = localStorage.getItem(SESSION_KEY);
        const savedBiz = localStorage.getItem(SESSION_BIZ_KEY);
        if (!savedId) {
            setBootChecking(false);
            return;
        }
        (async () => {
            try {
                const { data } = await api.get(`/sessions/${savedId}`);
                if (data.status === "complete" && data.report_id) {
                    // Already finished — clean up and go to report
                    localStorage.removeItem(SESSION_KEY);
                    localStorage.removeItem(SESSION_BIZ_KEY);
                    nav(`/report/${data.report_id}`);
                    return;
                }
                if (data.status === "error") {
                    localStorage.removeItem(SESSION_KEY);
                    localStorage.removeItem(SESSION_BIZ_KEY);
                    setBootChecking(false);
                    return;
                }
                setResumeInfo({
                    sessionId: savedId,
                    businessName:
                        savedBiz ||
                        data.business_intake?.business_name ||
                        "your business",
                    data,
                });
            } catch (err) {
                // session not found — wipe the stale key
                localStorage.removeItem(SESSION_KEY);
                localStorage.removeItem(SESSION_BIZ_KEY);
            } finally {
                setBootChecking(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startResume = (info) => {
        const d = info.data;
        setSessionId(info.sessionId);
        const resumedFlow = d.flow_type || d.business_intake?.flow_type || "business";
        setFlowType(resumedFlow);
        setIntake((prev) => ({ ...prev, ...(d.business_intake || {}) }));
        setHistory(d.history || []);
        setStepIndex(d.step_index || 0);
        setTotalSteps(d.total_steps || (resumedFlow === "career" ? 7 : 8));
        // restore any in-progress draft
        try {
            const draft = localStorage.getItem(`weave_draft_${info.sessionId}`);
            if (draft) setAnswer(draft);
        } catch {}
        if (d.status === "generating") {
            setPhase("generating");
            pollForReport(info.sessionId);
        } else if (d.pending_question) {
            setCurrentQ(d.pending_question);
            setPhase("conversation");
        } else {
            // Session exists but no first question yet — either the initial
            // background task is still running, or it died and the backend
            // will self-heal on the next GET. Show the conversation panel in
            // its "Reading what you said…" state and start polling so the
            // question appears as soon as it's ready.
            setPhase("conversation");
            (async () => {
                const q = await pollForNextQuestion(info.sessionId);
                if (q) setCurrentQ(q);
            })();
        }
        setResumeInfo(null);
        toast.success("Conversation restored.");
    };

    const discardResume = () => {
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(SESSION_BIZ_KEY);
        if (resumeInfo?.sessionId) {
            try {
                localStorage.removeItem(`weave_draft_${resumeInfo.sessionId}`);
            } catch {}
        }
        setResumeInfo(null);
    };

    const submitIntake = async (e) => {
        e.preventDefault();
        if (flowType === "career") {
            if (!intake.first_name || !intake.industry || !intake.restlessness) {
                toast.error("Please fill in your first name, world of work, and what's been niggling.");
                return;
            }
        } else if (!intake.business_name || !intake.what_you_do || !intake.plateau_description) {
            toast.error("Please fill in business, what you do, and the plateau.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = flowType === "career"
                ? {
                    flow_type: "career",
                    first_name: intake.first_name,
                    industry: intake.industry,
                    tenure: intake.tenure,
                    what_you_do: intake.industry || "career professional",
                    region: intake.region,
                    restlessness: intake.restlessness,
                    mission_statement: intake.mission_statement,
                }
                : { ...intake, flow_type: "business" };
            const { data } = await api.post("/diagnostic/start", { intake: payload });
            const sid = data.session_id;
            setSessionId(sid);
            try {
                localStorage.setItem(SESSION_KEY, sid);
                const label =
                    flowType === "career"
                        ? intake.first_name || "your second act"
                        : intake.business_name || "your business";
                localStorage.setItem(SESSION_BIZ_KEY, label);
            } catch {}
            setTotalSteps(data.total_steps);
            setStepIndex(0);
            // Move to conversation view immediately, show "thinking" whilst we poll for Q1
            setCurrentQ("");
            setPhase("conversation");
            const firstQ = await pollForNextQuestion(sid);
            if (firstQ) setCurrentQ(firstQ);
        } catch (err) {
            console.error(err);
            toast.error(
                err?.response?.data?.detail ||
                    "Could not start. Please try again — your answers are saved."
            );
            setPhase("intake");
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Poll GET /sessions/{id} until a new assistant question appears (or error).
     * Used after /start (waiting for Q1) and after /message (waiting for QN+1).
     * Returns the pending question string, or null on error/timeout.
     */
    const pollForNextQuestion = async (sid, priorQuestion = null) => {
        const start = Date.now();
        const MAX_MS = 180_000; // 3 min
        while (Date.now() - start < MAX_MS) {
            try {
                const { data } = await api.get(`/sessions/${sid}`);
                if (data.status === "error") {
                    toast.error(
                        "Something went wrong on our side — please retry sending. Your answers are saved."
                    );
                    return null;
                }
                if (
                    data.pending_question &&
                    data.pending_question !== priorQuestion
                ) {
                    return data.pending_question;
                }
                if (data.status === "complete" && data.report_id) {
                    // Skipped straight to report — shouldn't happen mid-flow but handled.
                    return null;
                }
            } catch {
                // ignore transient errors
            }
            await new Promise((r) => setTimeout(r, 2000));
        }
        toast.error("Taking longer than expected — please refresh; your work is saved.");
        return null;
    };

    const pollForReport = async (sid) => {
        if (pollingRef.current) return;
        pollingRef.current = true;
        const start = Date.now();
        const MAX_MS = 240_000; // 4 min safety
        while (Date.now() - start < MAX_MS) {
            try {
                const { data } = await api.get(`/sessions/${sid}/status`);
                if (data.status === "complete" && data.report_id) {
                    pollingRef.current = false;
                    // clean up session storage on success
                    localStorage.removeItem(SESSION_KEY);
                    localStorage.removeItem(SESSION_BIZ_KEY);
                    try {
                        localStorage.removeItem(`weave_draft_${sid}`);
                    } catch {}
                    nav(`/report/${data.report_id}`);
                    return;
                }
                if (data.status === "error") {
                    pollingRef.current = false;
                    toast.error(
                        "Couldn't finish your report — please try again. Your answers are still saved."
                    );
                    setPhase("conversation");
                    return;
                }
            } catch (err) {
                // keep polling on transient errors
            }
            await new Promise((r) => setTimeout(r, 3000));
        }
        pollingRef.current = false;
        toast.error(
            "Report is taking longer than expected. Please refresh — your work is saved."
        );
    };

    const submitAnswer = async (e) => {
        e?.preventDefault();
        if (!answer.trim()) {
            toast.error("Write a sentence or two — even rough is fine.");
            return;
        }
        const myAnswer = answer.trim();
        setHistory((h) => [...h, { q: currentQ, a: myAnswer }]);
        setAnswer("");
        if (draftKey) {
            try {
                localStorage.removeItem(draftKey);
            } catch {}
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/diagnostic/message", {
                session_id: sessionId,
                answer: myAnswer,
            });
            setStepIndex(data.step_index);
            if (data.is_complete) {
                setPhase("generating");
                if (data.report_id) {
                    localStorage.removeItem(SESSION_KEY);
                    localStorage.removeItem(SESSION_BIZ_KEY);
                    setTimeout(() => nav(`/report/${data.report_id}`), 600);
                } else {
                    pollForReport(sessionId);
                }
            } else {
                setCurrentQ(data.next_question);
            }
        } catch (err) {
            console.error(err);
            toast.error(
                err?.response?.data?.detail ||
                    "Something went wrong. Try sending again — your answer is saved."
            );
            setAnswer(myAnswer);
            setHistory((h) => h.slice(0, -1));
        } finally {
            setSubmitting(false);
        }
    };

    const isGenerating = phase === "generating";

    if (bootChecking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-weave-bg">
                <Loader2 className="w-6 h-6 animate-spin text-weave-teal" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-weave-bg">
            <Header minimal />
            <main className="flex-1 max-w-3xl mx-auto w-full px-6 md:px-8 py-12 md:py-16">
                {resumeInfo && (
                    <ResumeBanner
                        businessName={resumeInfo.businessName}
                        onResume={() => startResume(resumeInfo)}
                        onDiscard={discardResume}
                    />
                )}

                {phase === "intake" && !resumeInfo && (
                    <>
                        <div className="mb-10" data-testid="flow-toggle">
                            <p className="editorial-label text-weave-teal mb-3">
                                Where are you right now?
                            </p>
                            <div className="grid md:grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setFlowType("career")}
                                    className={`text-left p-5 border transition-colors ${
                                        flowType === "career"
                                            ? "border-weave-ink bg-weave-surface-alt"
                                            : "border-weave-border bg-weave-surface hover:border-weave-ink"
                                    }`}
                                    data-testid="flow-career"
                                >
                                    <p className="editorial-label text-weave-slate mb-1">Option 01</p>
                                    <p className="text-weave-ink font-medium">I'm a professional imagining a second act</p>
                                    <p className="text-sm text-weave-slate mt-1">Still employed. Restless. Wondering what you'd regret not building.</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFlowType("business")}
                                    className={`text-left p-5 border transition-colors ${
                                        flowType === "business"
                                            ? "border-weave-ink bg-weave-surface-alt"
                                            : "border-weave-border bg-weave-surface hover:border-weave-ink"
                                    }`}
                                    data-testid="flow-business"
                                >
                                    <p className="editorial-label text-weave-slate mb-1">Option 02</p>
                                    <p className="text-weave-ink font-medium">I run a business whose engine has stalled</p>
                                    <p className="text-sm text-weave-slate mt-1">Sales have flattened. Referrals are drying up. There's value left on the table.</p>
                                </button>
                            </div>
                        </div>
                        {flowType === "career" ? (
                            <CareerIntakeForm
                                intake={intake}
                                setIntake={setIntake}
                                submitting={submitting}
                                onSubmit={submitIntake}
                            />
                        ) : (
                            <IntakeForm
                                intake={intake}
                                setIntake={setIntake}
                                submitting={submitting}
                                onSubmit={submitIntake}
                            />
                        )}
                    </>
                )}

                {phase === "conversation" && !isGenerating && (
                    <div
                        className="animate-fade-in"
                        data-testid="conversation-panel"
                    >
                        <ProgressBar value={stepIndex} total={totalSteps} flow={flowType} />
                        <p
                            className="editorial-label text-weave-teal mt-12 mb-4"
                            data-testid="conversation-eyebrow"
                        >
                            {flowType === "career"
                                ? stepIndex < 3
                                    ? `Personal clarity · ${stepIndex + 1} of 3`
                                    : stepIndex < 5
                                    ? `Business clarity · ${stepIndex - 2} of 2`
                                    : `External clarity · ${stepIndex - 4} of 2`
                                : stepIndex < 5
                                ? `Why #${stepIndex + 1}`
                                : stepIndex < 7
                                ? "Strengths & assets"
                                : "Constraints as leverage"}
                        </p>
                        {currentQ ? (
                            <h2
                                className="text-3xl md:text-4xl font-light leading-snug text-weave-ink tracking-tight"
                                data-testid="conversation-question"
                                key={currentQ}
                            >
                                {currentQ}
                            </h2>
                        ) : (
                            <div
                                className="flex items-center gap-3 py-6"
                                data-testid="conversation-thinking"
                            >
                                <Loader2 className="w-5 h-5 animate-spin text-weave-teal" />
                                <p className="text-xl md:text-2xl text-weave-slate font-light tracking-tight">
                                    Reading what you said…
                                </p>
                            </div>
                        )}
                        <form onSubmit={submitAnswer} className="mt-10">
                            <Textarea
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                                rows={5}
                                placeholder="Take your time. Write what feels true, not what sounds polished."
                                className="bg-weave-surface border-weave-border focus-visible:ring-weave-teal text-base"
                                data-testid="answer-input"
                                autoFocus
                            />
                            <div className="mt-5 flex items-center justify-between">
                                <span className="text-xs text-weave-muted">
                                    {answer.length} chars · auto-saved as you
                                    type
                                </span>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-primary disabled:opacity-50"
                                    data-testid="answer-submit"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Thinking…
                                        </>
                                    ) : (
                                        <>
                                            Send
                                            <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        {history.length > 0 && (
                            <details className="mt-16">
                                <summary
                                    className="editorial-label text-weave-slate cursor-pointer select-none"
                                    data-testid="history-toggle"
                                >
                                    Conversation so far ({history.length})
                                </summary>
                                <ol className="mt-6 space-y-6 border-l border-weave-border pl-6">
                                    {history.map((h, i) => (
                                        <li key={i}>
                                            <p className="text-sm text-weave-slate">
                                                {h.q}
                                            </p>
                                            <p className="text-weave-ink mt-1">
                                                {h.a}
                                            </p>
                                        </li>
                                    ))}
                                </ol>
                            </details>
                        )}
                    </div>
                )}

                {isGenerating && (
                    <div
                        className="animate-fade-in py-20 md:py-28 text-center"
                        data-testid="generating-panel"
                    >
                        <Loader2 className="w-8 h-8 animate-spin mx-auto text-weave-teal" />
                        <p className="editorial-label text-weave-teal mt-8">
                            Weaving your report
                        </p>
                        <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink mt-4">
                            Reading what you said. Sizing the market. Writing
                            it up properly.
                        </h2>
                        <p className="text-weave-slate mt-4 max-w-md mx-auto">
                            This usually takes 30–90 seconds. Don't worry if
                            this tab closes — your answers are saved. You can
                            come back to{" "}
                            <span className="text-weave-ink">/diagnostic</span>{" "}
                            and pick up the report.
                        </p>
                    </div>
                )}
            </main>
        </div>
    );
}

function CareerIntakeForm({ intake, setIntake, submitting, onSubmit }) {
    return (
        <div className="animate-fade-in" data-testid="career-intake-form">
            <p className="editorial-label text-weave-teal mb-4">
                A gentle start
            </p>
            <h1 className="text-4xl md:text-5xl font-light tracking-tight text-weave-ink leading-tight">
                Before anything else, a few easy ones.
            </h1>
            <p className="mt-6 text-lg text-weave-slate leading-relaxed">
                No commitment. No wrong answers. Take your time — nothing here is scored.
            </p>
            <form onSubmit={onSubmit} className="mt-12 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <Label className="editorial-label text-weave-slate">First name</Label>
                        <Input value={intake.first_name} onChange={(e) => setIntake((v) => ({...v, first_name: e.target.value}))} className="mt-2 bg-weave-surface focus-visible:ring-weave-teal" data-testid="career-first-name" placeholder="Amit" />
                    </div>
                    <div>
                        <Label className="editorial-label text-weave-slate">Region</Label>
                        <Select value={intake.region} onValueChange={(v) => setIntake((s) => ({...s, region: v}))}>
                            <SelectTrigger className="mt-2 bg-weave-surface"><SelectValue /></SelectTrigger>
                            <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <div>
                    <Label className="editorial-label text-weave-slate">The world of work you know best</Label>
                    <Input value={intake.industry} onChange={(e) => setIntake((v) => ({...v, industry: e.target.value}))} placeholder="e.g. Corporate finance, HR leadership, engineering" className="mt-2 bg-weave-surface focus-visible:ring-weave-teal" data-testid="career-industry" />
                </div>
                <div>
                    <Label className="editorial-label text-weave-slate">How long you've been in it</Label>
                    <Input value={intake.tenure} onChange={(e) => setIntake((v) => ({...v, tenure: e.target.value}))} placeholder="e.g. 18 years" className="mt-2 bg-weave-surface focus-visible:ring-weave-teal" data-testid="career-tenure" />
                </div>
                <div>
                    <Label className="editorial-label text-weave-slate">What's been niggling at you lately</Label>
                    <Textarea value={intake.restlessness} onChange={(e) => setIntake((v) => ({...v, restlessness: e.target.value}))} rows={4} placeholder="Take your time. There's no right way to name a restlessness." className="mt-2 bg-weave-surface focus-visible:ring-weave-teal" data-testid="career-restlessness" />
                </div>
                <div>
                    <Label className="editorial-label text-weave-slate">Direction of travel · optional</Label>
                    <p className="text-sm text-weave-slate mt-1 mb-2 leading-relaxed">When you look back in ten years, what would you regret not having built?</p>
                    <Textarea value={intake.mission_statement} onChange={(e) => setIntake((v) => ({...v, mission_statement: e.target.value}))} rows={3} className="mt-2 bg-weave-surface focus-visible:ring-weave-teal" data-testid="career-mission" />
                </div>
                <div className="pt-4 flex items-center justify-between border-t border-weave-border">
                    <span className="text-sm text-weave-muted">Takes about 10 minutes · saved as you go</span>
                    <button type="submit" disabled={submitting || !intake.first_name || !intake.industry || !intake.restlessness} className="btn-primary disabled:opacity-50" data-testid="career-intake-submit">
                        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Starting…</> : <>Begin gently<ArrowRight className="w-4 h-4" /></>}
                    </button>
                </div>
            </form>
        </div>
    );
}

function IntakeForm({ intake, setIntake, submitting, onSubmit }) {
    return (
        <div className="animate-fade-in" data-testid="intake-form">
            <p className="editorial-label text-weave-teal mb-4">
                Step 01 · Tell us about the business
            </p>
            <h1 className="text-4xl md:text-5xl font-light tracking-tight text-weave-ink leading-tight">
                Before we ask the hard questions, a few easy ones.
            </h1>
            <p className="mt-6 text-lg text-weave-slate leading-relaxed">
                We'll keep this short. The more honestly you describe the
                plateau, the better the opportunity we can find. Your answers
                are saved as you go — you can close the tab and come back.
            </p>

            <form onSubmit={onSubmit} className="mt-12 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <Label
                            htmlFor="business_name"
                            className="editorial-label text-weave-slate"
                        >
                            Business name
                        </Label>
                        <Input
                            id="business_name"
                            value={intake.business_name}
                            onChange={(e) =>
                                setIntake((v) => ({
                                    ...v,
                                    business_name: e.target.value,
                                }))
                            }
                            placeholder="e.g. Ashford & Co"
                            className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                            data-testid="intake-business-name"
                        />
                    </div>
                    <div>
                        <Label
                            htmlFor="region"
                            className="editorial-label text-weave-slate"
                        >
                            Region
                        </Label>
                        <Select
                            value={intake.region}
                            onValueChange={(v) =>
                                setIntake((s) => ({ ...s, region: v }))
                            }
                        >
                            <SelectTrigger
                                id="region"
                                className="mt-2 bg-weave-surface"
                                data-testid="intake-region"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {REGIONS.map((r) => (
                                    <SelectItem key={r} value={r}>
                                        {r}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div>
                    <Label
                        htmlFor="what_you_do"
                        className="editorial-label text-weave-slate"
                    >
                        What you actually do (one or two sentences)
                    </Label>
                    <Textarea
                        id="what_you_do"
                        value={intake.what_you_do}
                        onChange={(e) =>
                            setIntake((v) => ({
                                ...v,
                                what_you_do: e.target.value,
                            }))
                        }
                        rows={2}
                        placeholder="We design and manufacture timber-frame outbuildings for residential clients in the south-east of England."
                        className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                        data-testid="intake-what-you-do"
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <Label
                            htmlFor="current_customers"
                            className="editorial-label text-weave-slate"
                        >
                            Who buys from you today
                        </Label>
                        <Input
                            id="current_customers"
                            value={intake.current_customers}
                            onChange={(e) =>
                                setIntake((v) => ({
                                    ...v,
                                    current_customers: e.target.value,
                                }))
                            }
                            placeholder="Mostly 40-55 year-old homeowners in Surrey/Kent"
                            className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                            data-testid="intake-current-customers"
                        />
                    </div>
                    <div>
                        <Label
                            htmlFor="revenue_range"
                            className="editorial-label text-weave-slate"
                        >
                            Current revenue range
                        </Label>
                        <Select
                            value={intake.revenue_range}
                            onValueChange={(v) =>
                                setIntake((s) => ({ ...s, revenue_range: v }))
                            }
                        >
                            <SelectTrigger
                                id="revenue_range"
                                className="mt-2 bg-weave-surface"
                                data-testid="intake-revenue-range"
                            >
                                <SelectValue placeholder="Choose a range" />
                            </SelectTrigger>
                            <SelectContent>
                                {REVENUE_RANGES.map((r) => (
                                    <SelectItem key={r} value={r}>
                                        {r}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div>
                    <Label
                        htmlFor="plateau"
                        className="editorial-label text-weave-slate"
                    >
                        Describe the plateau (in your own words)
                    </Label>
                    <Textarea
                        id="plateau"
                        value={intake.plateau_description}
                        onChange={(e) =>
                            setIntake((v) => ({
                                ...v,
                                plateau_description: e.target.value,
                            }))
                        }
                        rows={4}
                        placeholder="We've been at the same revenue for three years. Referrals are drying up. The market feels saturated, and we're not sure where the next £500k comes from."
                        className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                        data-testid="intake-plateau"
                    />
                </div>

                <div>
                    <Label
                        htmlFor="mission"
                        className="editorial-label text-weave-slate"
                    >
                        Direction of travel · optional
                    </Label>
                    <p className="text-sm text-weave-slate mt-1 mb-2 leading-relaxed">
                        When you started, did you know what impact you wanted
                        to make? Is that still why you're here — or has the
                        direction shifted?
                    </p>
                    <Textarea
                        id="mission"
                        value={intake.mission_statement}
                        onChange={(e) =>
                            setIntake((v) => ({
                                ...v,
                                mission_statement: e.target.value,
                            }))
                        }
                        rows={3}
                        placeholder="The engine has stalled, but if the direction is still right, what is it?"
                        className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                        data-testid="intake-mission"
                    />
                    <details className="mt-2">
                        <summary className="text-xs text-weave-teal cursor-pointer select-none hover:underline">
                            Not sure? Try this →
                        </summary>
                        <p className="text-sm text-weave-slate mt-2 leading-relaxed border-l-2 border-weave-yellow pl-3">
                            What would feel like a failure even if you got
                            rich doing it? Whatever the answer points away from
                            — that's the direction.
                        </p>
                    </details>
                </div>

                <div className="pt-4 flex items-center justify-between border-t border-weave-border">
                    <span className="text-sm text-weave-muted">
                        Takes about 10 minutes · saved as you go
                    </span>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-primary disabled:opacity-50"
                        data-testid="intake-submit"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Starting…
                            </>
                        ) : (
                            <>
                                Begin the conversation
                                <ArrowRight className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
